import React, { useState, useEffect } from 'react';
import {
  OrderNeedingInvoice,
  CreateInvoiceData,
  createInvoice,
} from '../services/quickbooksService';
import './InvoiceCreationModal.css';

interface InvoiceCreationModalProps {
  order: OrderNeedingInvoice;
  onClose: () => void;
  onSuccess: (invoiceNumber: string, sent: boolean) => void;
}

const InvoiceCreationModal: React.FC<InvoiceCreationModalProps> = ({
  order,
  onClose,
  onSuccess,
}) => {
  const [shippingCost, setShippingCost] = useState<string>('');
  const [additionalFee, setAdditionalFee] = useState<string>('');
  const [feeDescription, setFeeDescription] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showShippingWarning, setShowShippingWarning] = useState(false);

  // Pre-populate shipping cost from backend calculation
  useEffect(() => {
    if (order.estimated_shipping !== undefined) {
      setShippingCost(order.estimated_shipping.toFixed(2));
    }
  }, [order.estimated_shipping]);

  // Calculate default due date from ship date + payment terms
  useEffect(() => {
    // Use the pre-calculated due date from the backend if available
    if (order.calculated_due_date) {
      setDueDate(order.calculated_due_date);
    } else {
      // Fallback: 30 days from today
      const defaultDue = new Date();
      defaultDue.setDate(defaultDue.getDate() + 30);
      setDueDate(defaultDue.toISOString().split('T')[0]);
    }
  }, [order.calculated_due_date]);

  // Format payment terms for display (handle various case formats)
  const formatPaymentTerms = (terms?: string): string => {
    if (!terms) return 'Net 30';
    // Normalize: lowercase, replace spaces/dashes with underscores
    const normalized = terms.toLowerCase().replace(/[\s-]/g, '_');
    const termsMap: Record<string, string> = {
      'due_on_receipt': 'Due on Receipt',
      'net_10': 'Net 10',
      'net_15': 'Net 15',
      'net_30': 'Net 30',
      'net_45': 'Net 45',
      'net_60': 'Net 60',
    };
    return termsMap[normalized] || terms;
  };

  // Calculate totals
  const subtotal = order.items.reduce(
    (sum, item) => sum + parseFloat(item.line_total || '0'),
    0
  );
  const shippingAmount = parseFloat(shippingCost) || 0;
  const feeAmount = parseFloat(additionalFee) || 0;

  // Tax calculation (8.0% Stevens County, WA — if not tax exempt)
  const taxRate = order.customer_tax_exempt ? 0 : 0.08;
  const taxableAmount = subtotal + shippingAmount + feeAmount;
  const taxAmount = order.customer_tax_exempt ? 0 : taxableAmount * taxRate;

  const total = subtotal + shippingAmount + feeAmount + taxAmount;

  // Check if shipping warning should show
  const isFreightOrder =
    order.shipping_method &&
    (order.shipping_method.toLowerCase().includes('ltl') ||
      order.shipping_method.toLowerCase().includes('freight') ||
      order.shipping_method.toLowerCase().includes('ship'));

  const isPickupOrder =
    order.shipping_method &&
    (order.shipping_method.toLowerCase().includes('will call') ||
      order.shipping_method.toLowerCase().includes('pickup'));

  const shouldWarnShipping = isFreightOrder && !isPickupOrder && shippingAmount === 0;

  const handleSubmit = async (sendImmediately: boolean) => {
    // Validate fee description if fee is present
    if (feeAmount > 0 && !feeDescription.trim()) {
      setError('Please enter a description for the additional fee');
      return;
    }

    // Show warning for zero shipping on freight orders
    if (shouldWarnShipping && !showShippingWarning) {
      setShowShippingWarning(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data: CreateInvoiceData = {
        shipping_cost: shippingAmount,
        additional_fee: feeAmount || undefined,
        additional_fee_description: feeDescription || undefined,
        notes: notes || undefined,
        due_date: dueDate || undefined,
        send_immediately: sendImmediately,
      };

      const result = await createInvoice(order.id, data);

      if (result.success) {
        onSuccess(result.invoice.invoice_number, result.invoice.sent);
      } else {
        setError('Failed to create invoice');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="invoice-modal-overlay" onClick={onClose}>
      <div className="invoice-modal" onClick={(e) => e.stopPropagation()}>
        <div className="invoice-modal-header">
          <h2>Create Invoice for Order #{order.order_number}</h2>
          <button className="invoice-modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="invoice-modal-body">
          {/* Customer Info */}
          <section className="invoice-section">
            <h3>Customer</h3>
            <div className="customer-info">
              <strong>{order.customer_name}</strong>
              {order.customer_address && (
                <p>
                  {order.customer_address}
                  {order.customer_city && `, ${order.customer_city}`}
                  {order.customer_state && `, ${order.customer_state}`}
                  {order.customer_zip && ` ${order.customer_zip}`}
                </p>
              )}
              {order.customer_email && <p>Email: {order.customer_email}</p>}
            </div>
          </section>

          <hr />

          {/* Order Items */}
          <section className="invoice-section">
            <h3>Order Items</h3>
            <table className="invoice-items-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item) => {
                  // Determine if this is a bagged item that should show bag pricing
                  const isBaggedItem = item.package_type &&
                    (item.package_type.includes('BAG') || item.package_type.includes('Bag'));
                  const hasBagPricing = isBaggedItem &&
                    item.price_per_package &&
                    parseFloat(item.price_per_package) > 0 &&
                    item.number_of_packages > 0;

                  return (
                    <tr key={item.id}>
                      <td>
                        {item.feed_name}
                        {item.package_type && ` - ${item.package_type}`}
                        {item.is_pelleted && ' (Pelleted)'}
                      </td>
                      {hasBagPricing ? (
                        <>
                          <td>{item.number_of_packages} bags</td>
                          <td>{formatCurrency(parseFloat(item.price_per_package!))}/bag</td>
                        </>
                      ) : (
                        <>
                          <td>{item.quantity_lbs.toLocaleString()} lb</td>
                          <td>{formatCurrency(parseFloat(item.unit_price))}/lb</td>
                        </>
                      )}
                      <td>{formatCurrency(parseFloat(item.line_total))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="invoice-subtotal">
              <span>Subtotal:</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
          </section>

          <hr />

          {/* Shipping & Fees */}
          <section className="invoice-section">
            <h3>Shipping & Additional Charges</h3>

            <div className="shipping-method-display">
              <strong>Shipping Method:</strong> {order.shipping_method || 'Not specified'}
            </div>

            <div className="invoice-form-row">
              <label htmlFor="shippingCost">Shipping Cost:</label>
              <div className="shipping-cost-input">
                <div className="currency-input">
                  <span className="currency-symbol">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    id="shippingCost"
                    value={shippingCost}
                    onChange={(e) => {
                      setShippingCost(e.target.value);
                      setShowShippingWarning(false);
                    }}
                    onBlur={(e) => {
                      const val = parseFloat(e.target.value);
                      if (!isNaN(val) && val >= 0) {
                        setShippingCost(val.toFixed(2));
                      }
                    }}
                    placeholder="0.00"
                  />
                </div>
                {order.shipping_breakdown && (
                  <span className="shipping-breakdown-hint">
                    Est: {order.shipping_breakdown.breakdown_text}
                  </span>
                )}
              </div>
            </div>

            {showShippingWarning && (
              <div className="shipping-warning">
                <span className="warning-icon">⚠️</span>
                Shipping cost is $0 for freight order - continue anyway?
                <div className="warning-actions">
                  <button
                    type="button"
                    className="btn-warning-continue"
                    onClick={() => handleSubmit(false)}
                    disabled={loading}
                  >
                    Yes, Save as Draft
                  </button>
                  <button
                    type="button"
                    className="btn-warning-cancel"
                    onClick={() => setShowShippingWarning(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="invoice-form-row">
              <label htmlFor="additionalFee">Additional Fee:</label>
              <div className="currency-input">
                <span className="currency-symbol">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  id="additionalFee"
                  value={additionalFee}
                  onChange={(e) => setAdditionalFee(e.target.value)}
                  onBlur={(e) => {
                    const val = parseFloat(e.target.value);
                    if (!isNaN(val) && val >= 0) {
                      setAdditionalFee(val.toFixed(2));
                    }
                  }}
                  placeholder="0.00"
                />
              </div>
            </div>

            {feeAmount > 0 && (
              <div className="invoice-form-row">
                <label htmlFor="feeDescription">Fee Description:</label>
                <input
                  type="text"
                  id="feeDescription"
                  value={feeDescription}
                  onChange={(e) => setFeeDescription(e.target.value)}
                  placeholder="e.g., Pallet fee, Rush order fee"
                  className={feeAmount > 0 && !feeDescription ? 'input-error' : ''}
                />
              </div>
            )}
          </section>

          <hr />

          {/* Tax Info */}
          <section className="invoice-section invoice-tax-section">
            <div className="tax-info">
              <span>
                Tax Exempt:{' '}
                {order.customer_tax_exempt ? (
                  <span className="tax-exempt-yes">✓ Yes</span>
                ) : (
                  <span className="tax-exempt-no">No (8.5%)</span>
                )}
              </span>
              <span>Tax: {formatCurrency(taxAmount)}</span>
            </div>

            <div className="invoice-total">
              <strong>TOTAL:</strong>
              <strong>{formatCurrency(total)}</strong>
            </div>
          </section>

          <hr />

          {/* Notes & Due Date */}
          <section className="invoice-section">
            <div className="invoice-form-row">
              <label htmlFor="notes">Invoice Notes (optional):</label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Thank you for your order!"
                rows={2}
              />
            </div>

            <div className="invoice-form-row">
              <label htmlFor="dueDate">Due Date:</label>
              <div className="due-date-input">
                <input
                  type="date"
                  id="dueDate"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
                <span className="payment-terms-hint">
                  {formatPaymentTerms(order.payment_terms)}
                  {order.ship_by_date && ` from ${formatDate(order.shipped_date || order.ship_by_date)}`}
                </span>
              </div>
            </div>
          </section>

          {error && <div className="invoice-error">{error}</div>}
        </div>

        <div className="invoice-modal-footer">
          <div className="footer-help">
            <p>• <strong>Draft:</strong> Save invoice to review/edit before sending</p>
            <p>• <strong>Send:</strong> Create and email invoice to customer now</p>
          </div>
          <div className="footer-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-draft"
              onClick={() => handleSubmit(false)}
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Save as Draft'}
            </button>
            <button
              type="button"
              className="btn-send"
              onClick={() => handleSubmit(true)}
              disabled={loading || !order.customer_email}
              title={!order.customer_email ? 'Customer has no email address' : ''}
            >
              {loading ? 'Sending...' : 'Create & Send to Customer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceCreationModal;
